/*
    Copyright (c) Computer-L.A.N. GmbH (http://www.computer-lan.de).
    Alle Rechte vorbehalten.
*/

import { useEffect, useState } from "react";
import PromiseLadeTypen, { Typ } from "../../../Cloud/DomainDrivenDesign/Queries/PromiseLadeTypen";
import { Konto } from "../../../Management/Mandanten/Queries/LadeEigenesKonto";
import { Mandant } from "../../../Management/Mandanten/Queries/LadeOeffentlichenMandant";
import SystemClient from "../../SystemInfo/SystemClient";
import { useAppKontoContext } from "../AppKontoContext";
import { useAppMandantContext } from "../AppMandantContext";
import AppContextChangeTracker from "./AppContextChangeTracker";
import IAppContext, { IAppOptions } from "./IAppContext";
import IAppContextChangeTracker from "./IAppContextChangeTracker";

interface AppContextInstanceState {
    typen?: Typ[];
    version: string;
    zeigeAppHeader: boolean;
    options: IAppOptions;
}

export default class AppContextInstance implements IAppContext {
    private static readonly appContextChangeTracker: IAppContextChangeTracker = new AppContextChangeTracker();
    private readonly _mandant: Mandant | undefined;
    private readonly _konto: Konto | undefined;
    private readonly _ladeTypen: PromiseLadeTypen;
    private readonly _state: AppContextInstanceState;
    private readonly _setState: React.Dispatch<React.SetStateAction<AppContextInstanceState>>;
    private readonly _systemClient: SystemClient;

    public constructor() {
        this._mandant = useAppMandantContext().mandant;
        this._konto = useAppKontoContext().konto;
        [this._state, this._setState] = useState<AppContextInstanceState>({
            version: "",
            zeigeAppHeader: false,
            options: {
                appVersion: "",
                instanzId: "",
                reCaptchaV3: { isDisabled: true, siteKey: "" },
                stripe: { livemode: false, publicKey: "" },
                pollingIntervallInSeconds: 60
            }
        });
        this._ladeTypen = new PromiseLadeTypen();
        this._systemClient = new SystemClient();

        const hatSitzung = Boolean(this._mandant) && Boolean(this._konto);
        useEffect(() => {
            this.onHatSitzungChanged(hatSitzung);
        }, [hatSitzung]);
        useEffect(() => {
            this.setzeOptions()
        }, []);
    }

    public get changeTracker(): IAppContextChangeTracker {
        return AppContextInstance.appContextChangeTracker;
    }

    public get typen(): Promise<Typ[]> {
        if (!this._state.typen) {
            return this._ladeTypen
                .execute(undefined)
                .then(typen => {
                    this.onTypenGeladen(typen);
                    return typen;
                });
        }

        return Promise.resolve(this._state.typen);
    }

    public get zeigeAppHeader(): boolean {
        return this._state.zeigeAppHeader;
    }

    public get version(): string {
        return this._state.version;
    }

    public get options(): IAppOptions {
        return this._state.options;
    }

    public setzeZeigeAppHeader(zeigeAppHeader: boolean) {
        this._setState(prevState => ({ ...prevState, zeigeAppHeader }));
    }

    private readonly onTypenGeladen = (typen: Typ[]) => {
        this._setState(prevState => ({ ...prevState, typen }));
    };

    private readonly onHatSitzungChanged = (hatSitzung: boolean) => {
        if (hatSitzung && !this.typen) {
            this._ladeTypen.execute(undefined).then(this.onTypenGeladen);
        }
    };

    private readonly setzeOptions = async () => {
        const response = await this._systemClient.ermittleAppOptions();
        const options = await response.json();
        this._setState(prevState => ({ ...prevState, options }));
    };
}
